import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axios";
import EntityManagement from "../../components/formTemplates/EntityManagement";
import EntityTable from "../../components/templateComponents/EntityTable";
import useDropdownFetch from "../../components/apiFetch/useDropdownFetch";
import {
  entitiesConfig,
  staticPhoneExtensions,
} from "../../components/templateComponents/constants";

const ServiceProviders = () => {
  const entityConfig = entitiesConfig.serviceProviders;

  const [formData, setFormData] = useState(() => {
    const initialData = {};
    entityConfig.formFields.forEach((field) => {
      initialData[field.name] = "";
    });
    return initialData;
  });
  const [errors, setErrors] = useState({});
  const [entities, setEntities] = useState([]);
  const [selectedWorkdays, setSelectedWorkdays] = useState([]);
  const [dropdownData, setDropdownData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);

  const firstFormFieldRef = useRef(null);

  const vasOptions = useDropdownFetch(entityConfig.dropdowns.vas);
  const shopOptions = useDropdownFetch(entityConfig.dropdowns.shop);
  const statusOptions = useDropdownFetch(entityConfig.dropdowns.status);
  const domainOptions = useDropdownFetch(entityConfig.dropdowns.domain);

  useEffect(() => {
    const newDropdownData = {
      vas: vasOptions.options || [],
      shop: shopOptions.options || [],
      status: statusOptions.options || [],
      domain: domainOptions.options || [],
      phone_ext: staticPhoneExtensions,
    };

    const hasChanged =
      JSON.stringify(dropdownData) !== JSON.stringify(newDropdownData);

    if (hasChanged) {
      setDropdownData(newDropdownData);
    }
  }, [vasOptions, shopOptions, statusOptions, domainOptions]);

  // ✅ Simplified phone validation
  const validatePhoneFields = () => {
    const newErrors = {};
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!formData.phone_ext) {
      newErrors.phone_ext = "Please select a phone extension.";
    }

    if (!formData.phone_no || !phoneRegex.test(formData.phone_no)) {
      newErrors.phone_no =
        "Enter a valid 10-digit phone number starting with 6–9.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchEntities = async () => {
    try {
      const response = await axiosInstance.post(entityConfig.endpoints.fetch, {
        view_type: "view all",
      });
      const fetchedEntities = response.data.Data || [];
      const formattedEntities = fetchedEntities.map((entity) => ({
        ...entity,
        workdays: entity.workdays ? entity.workdays.split(", ") : [],
      }));
      setEntities(formattedEntities);
    } catch (error) {
      console.error("Error fetching service providers:", error);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, []);

  const handleUpdate = async (entityId, updatedData) => {
    const currentEntity = entities.find(
      (entity) => entity.service_provider_id === entityId
    );

    try {
      const response = await axiosInstance.post(entityConfig.endpoints.update, {
        service_provider_id: entityId,
        ...updatedData,
      });

      if (response.data.Status === "Success") {
        await fetchEntities();
        resetForm();
        setIsTableModalOpen(false);
      } else {
        console.error("Failed to update entity:", response.data);
      }
    } catch (error) {
      console.error("Error updating entity:", error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formattedFormData = {
      ...formData,
      vas_id: parseInt(formData.vas_id, 10),
      shop_id: parseInt(formData.shop_id, 10),
      status_id: parseInt(formData.status_id, 10),
      domain_id: parseInt(formData.domain_id, 10),
      workdays: selectedWorkdays.map((day) => day.toString()),
    };

    if (editMode) {
      await handleUpdate(editingEntityId, formattedFormData);
    } else {
      try {
        const response = await axiosInstance.post(
          entityConfig.endpoints.create,
          formattedFormData
        );

        if (response.data.Status === "Success ") {
          const newEntity = {
            service_provider_id: response.data.service_provider_id,
            ...formattedFormData,
            workdays: selectedWorkdays,
          };

          setEntities([...entities, newEntity]);
          resetForm();
        } else {
          console.error("Failed to add new entity:", response.data);
        }
      } catch (error) {
        console.error("Error saving data:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData(() => {
      const initialData = {};
      entityConfig.formFields.forEach((field) => {
        initialData[field.name] = "";
      });
      return initialData;
    });
    setSelectedWorkdays([]);
    setEditMode(false);
    setEditingEntityId(null);
  };

  const handleEdit = (entityId) => {
    const entityToEdit = entities.find(
      (entity) => entity.service_provider_id === entityId
    );
    if (entityToEdit) {
      const updatedFormData = {};
      entityConfig.formFields.forEach((field) => {
        updatedFormData[field.name] = entityToEdit[field.name] || "";
      });
      setFormData(updatedFormData);
      setSelectedWorkdays(entityToEdit.workdays || []);
      setEditMode(true);
      setEditingEntityId(entityId);
      if (firstFormFieldRef.current) {
        firstFormFieldRef.current.focus();
      }
      setIsTableModalOpen(false);
    }
  };

  return (
    <div className="service-providers">
      <EntityManagement
        entityName={entityConfig.name}
        formFields={entityConfig.formFields}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
        dropdownData={dropdownData}
        setDropdownData={setDropdownData}
        showWorkdays={true}
        selectedWorkdays={selectedWorkdays}
        setSelectedWorkdays={setSelectedWorkdays}
        handleViewTableClick={() => setIsTableModalOpen(true)}
        firstFormFieldRef={firstFormFieldRef}
        onEdit={handleEdit}
        successMessage={successMessage}
        errors={errors}
      />
      {isTableModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <button onClick={() => setIsTableModalOpen(false)}>Close</button>
            <EntityTable
              data={entities}
              columns={entityConfig.columns}
              onEdit={handleEdit}
              keyField="service_provider_id"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceProviders;
